import type { ModuleTransaction, MultisigTransaction } from '@safe-global/store/gateway/AUTO_GENERATED/transactions'
import { TxProposalChip } from '@/features/proposers'
import { SwapFeature, useIsExpiredSwap } from '@/features/swap'
import { Typography } from '@/components/ui/typography'
import type { ReactElement } from 'react'

import css from './styles.module.css'
import DateTime from '@/components/common/DateTime'
import TxInfo from '@/components/transactions/TxInfo'
import { isMultisigExecutionInfo, isTxQueued } from '@/utils/transaction-guards'
import { TxTypeIcon, TxTypeText } from '@/components/transactions/TxType'
import classNames from 'classnames'
import { isImitation, isTrustedTx } from '@/utils/transactions'
import MaliciousTxWarning from '../MaliciousTxWarning'
import QueueActions from './QueueActions'
import useIsPending from '@/hooks/useIsPending'
import TxConfirmations from '../TxConfirmations'
import { useHasFeature } from '@/hooks/useChains'
import TxStatusLabel from '@/components/transactions/TxStatusLabel'
import { FEATURES } from '@safe-global/utils/utils/chains'
import { ellipsis } from '@safe-global/utils/utils/formatters'
import {
  useHnQueueAssessmentResult,
  useShowHypernativeAssessment,
  useHypernativeOAuth,
  HypernativeFeature,
} from '@/features/hypernative'
import { getSafeTxHashFromTxId } from '@/utils/transactions'
import { SafenetChecksFeature, useIsSafenetChecksEnabled } from '@/features/safenet-checks'
import { useLoadFeature } from '@/features/__core__/useLoadFeature'

type TxSummaryProps = {
  isConflictGroup?: boolean
  isBulkGroup?: boolean
  item: ModuleTransaction | MultisigTransaction
}

const TxSummary = ({ item, isConflictGroup, isBulkGroup }: TxSummaryProps): ReactElement => {
  const { StatusLabel } = useLoadFeature(SwapFeature)
  const hasDefaultTokenlist = useHasFeature(FEATURES.DEFAULT_TOKENLIST)
  const { HnQueueAssessment } = useLoadFeature(HypernativeFeature)
  const safenet = useLoadFeature(SafenetChecksFeature)
  const isSafenetEnabled = useIsSafenetChecksEnabled()

  const tx = item.transaction
  const isQueue = isTxQueued(tx.txStatus)
  const nonce = isMultisigExecutionInfo(tx.executionInfo) ? tx.executionInfo.nonce : undefined
  const isTrusted = !hasDefaultTokenlist || isTrustedTx(tx)
  const isImitationTransaction = isImitation(tx)
  const showWarning = isImitationTransaction || !isTrusted
  const isPending = useIsPending(tx.id)
  const executionInfo = isMultisigExecutionInfo(tx.executionInfo) ? tx.executionInfo : undefined
  const expiredSwap = useIsExpiredSwap(tx.txInfo)

  // Extract safeTxHash for assessment
  const safeTxHash = tx.id ? getSafeTxHashFromTxId(tx.id) : undefined
  const assessment = useHnQueueAssessmentResult(safeTxHash)
  const { isAuthenticated } = useHypernativeOAuth()
  const showAssessment = useShowHypernativeAssessment() && isQueue
  // Bulk-group rows hide the cell via CSS; skipping the mount also skips the chain read.
  const showSafenetStatus = isSafenetEnabled && isQueue && !isBulkGroup && !!safeTxHash

  return (
    <div
      data-testid="transaction-item"
      className={classNames(css.gridContainer, {
        // Top-level queue rows carry the most cells, so they get their own narrow-width template.
        [css.queue]: isQueue && !isConflictGroup && !isBulkGroup,
        [css.history]: !isQueue,
        [css.conflictGroup]: isConflictGroup,
        [css.bulkGroup]: isBulkGroup,
        [css.untrusted]: showWarning,
        [css.withAssessment]: showAssessment,
        [css.withSafenet]: showSafenetStatus,
      })}
      id={tx.id}
    >
      {/* The warning claims the same cell, so the nonce yields to it rather than stacking underneath. */}
      {nonce !== undefined && !isConflictGroup && !showWarning && (
        <div data-testid="nonce" className={css.nonce} style={{ gridArea: 'nonce' }}>
          {nonce}
        </div>
      )}

      {showWarning && (
        <div data-testid="warning" style={{ gridArea: 'nonce' }}>
          <MaliciousTxWarning withTooltip={!isImitationTransaction} />
        </div>
      )}

      <div data-testid="tx-type" className={css.type} style={{ gridArea: 'type' }}>
        {/* Composed from TxType's icon and text rather than the combined export, so this file owns the
            label's class and can drop it on phones while keeping the icon. */}
        <div className={css.typeRow}>
          <TxTypeIcon tx={tx} />
          <span className={css.typeLabel}>
            <TxTypeText tx={tx} />
          </span>
        </div>

        {tx.note && (
          <Typography
            variant="paragraph-small"
            className={classNames('text-[var(--color-text-secondary)]', css.note)}
            title={tx.note}
          >
            {ellipsis(tx.note, 25)}
          </Typography>
        )}
      </div>

      <div data-testid="tx-info" className={css.info} style={{ gridArea: 'info' }}>
        <TxInfo info={tx.txInfo} />
      </div>

      <div data-testid="tx-date" className={css.date} style={{ gridArea: 'date' }}>
        <DateTime value={tx.timestamp} />
      </div>

      {isQueue && executionInfo && (
        <div style={{ gridArea: 'confirmations' }}>
          {executionInfo.confirmationsSubmitted > 0 || isPending ? (
            <TxConfirmations
              submittedConfirmations={executionInfo.confirmationsSubmitted}
              requiredConfirmations={executionInfo.confirmationsRequired}
            />
          ) : (
            <TxProposalChip />
          )}
        </div>
      )}

      {showAssessment && safeTxHash && (
        <div style={{ gridArea: 'assessment' }} className={css.assessment}>
          <HnQueueAssessment safeTxHash={safeTxHash} assessment={assessment} isAuthenticated={isAuthenticated} />
        </div>
      )}

      {showSafenetStatus && safeTxHash && (
        <div style={{ gridArea: 'safenet' }} className={css.safenet}>
          <safenet.SafenetQueueStatus safeTxHash={safeTxHash} timestampMs={tx.timestamp} />
        </div>
      )}

      {!isQueue && (
        <div className={css.status} style={{ gridArea: 'status' }}>
          <TxStatusLabel tx={tx} />
        </div>
      )}

      {/* A queue row's status takes the action's cell, so pending rows keep the same tracks as the rest. */}
      {isQueue && (
        <div className={css.actions} style={{ gridArea: 'actions' }}>
          {expiredSwap ? (
            <StatusLabel status="expired" />
          ) : (
            <>
              {isPending && <TxStatusLabel tx={tx} />}
              <QueueActions tx={tx} />
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default TxSummary
